"use client";

import { FormEvent, useEffect, useState } from "react";
import { CircleAlert, Clock3, LifeBuoy } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { getOrders, OrderRecord } from "@/features/orders/api";
import {
  SupportCategory,
  SupportTicket,
  createSupportTicket,
  getSupportTickets,
} from "@/features/support/api";

const categoryLabels: Record<SupportCategory, string> = {
  GENERAL: "일반 문의",
  TRANSACTION: "거래 기록 지원",
  WELFARE: "동물복지 긴급 신고",
};
const sla = {
  GENERAL: "첫 확인 48시간 · 처리 목표 5일",
  TRANSACTION: "첫 확인 24시간 · 처리 목표 3일",
  WELFARE: "첫 확인 1시간 · 처리 목표 24시간",
};

export default function SupportPage() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [category, setCategory] = useState<SupportCategory>("GENERAL");
  const [orderId, setOrderId] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = async () => {
    const [nextTickets, nextOrders] = await Promise.all([
      getSupportTickets(),
      getOrders().catch(() => []),
    ]);
    setTickets(nextTickets);
    setOrders(nextOrders);
  };
  useEffect(() => {
    void load().catch(() =>
      setError("로그인 후 문의 내역을 확인할 수 있습니다."),
    );
  }, []);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await createSupportTicket({
        category,
        orderId: orderId || undefined,
        subject,
        description,
      });
      setSubject("");
      setDescription("");
      await load();
    } catch {
      setError("입력 내용과 주문 접근 권한을 확인해주세요.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="support-page">
      <SiteHeader compact />
      <section className="support-hero">
        <div className="shell">
          <LifeBuoy />
          <span className="eyebrow">CUSTOMER SUPPORT</span>
          <h1>도움 요청</h1>
          <p>문의 유형별 확인 목표 시간을 확인하고 처리 상태를 추적하세요.</p>
        </div>
      </section>
      <section className="support-body shell">
        <aside className="welfare-warning">
          <CircleAlert />
          <div>
            <strong>생명·신체에 즉각적인 위험이 있나요?</strong>
            <p>
              온독 신고만 기다리지 말고 가까운 동물병원, 경찰 또는 관할
              동물보호기관에 직접 연락하세요. 온독은 응급 구조기관이 아닙니다.
            </p>
          </div>
        </aside>
        <form className="support-form" onSubmit={submit}>
          <h2>새 문의 접수</h2>
          <label>
            유형
            <select
              value={category}
              onChange={(event) =>
                setCategory(event.target.value as SupportCategory)
              }
            >
              {Object.entries(categoryLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <small>{sla[category]}</small>
          </label>
          <label>
            관련 주문 (선택)
            <select
              value={orderId}
              onChange={(event) => setOrderId(event.target.value)}
            >
              <option value="">주문 없음</option>
              {orders.map((order) => (
                <option key={order.id} value={order.id}>
                  강아지 #{order.dogId} · {order.id.slice(0, 8)}
                </option>
              ))}
            </select>
          </label>
          <label>
            제목
            <input
              required
              minLength={5}
              maxLength={160}
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
            />
          </label>
          <label className="wide">
            상세 내용
            <textarea
              required
              minLength={20}
              maxLength={4000}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
          <button disabled={busy}>
            {busy
              ? "접수 중"
              : category === "WELFARE"
                ? "긴급 신고 접수"
                : "문의 접수"}
          </button>
        </form>
        {error && (
          <p className="support-error" role="alert">
            {error}
          </p>
        )}
        <div className="support-list">
          <h2>내 문의 내역</h2>
          {tickets.length === 0 ? (
            <p>접수한 문의가 없습니다.</p>
          ) : (
            tickets.map((ticket) => (
              <article
                key={ticket.id}
                className={ticket.priority === "EMERGENCY" ? "emergency" : ""}
              >
                <span>
                  {categoryLabels[ticket.category]} · {ticket.status}
                </span>
                <h3>{ticket.subject}</h3>
                <p>{ticket.description}</p>
                <small>
                  <Clock3 /> 첫 확인 목표{" "}
                  {new Date(ticket.firstResponseDueAt).toLocaleString("ko-KR")}{" "}
                  · 처리 목표{" "}
                  {new Date(ticket.resolutionDueAt).toLocaleString("ko-KR")}
                </small>
                {ticket.adminNote && (
                  <blockquote>{ticket.adminNote}</blockquote>
                )}
              </article>
            ))
          )}
        </div>
      </section>
    </main>
  );
}
